import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/lib/useEntitlements";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { EligibilityPill } from "@/components/EligibilityPill";
import { MatchScore } from "@/components/MatchScore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ChipsInput from "@/components/ChipsInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchPlanCard, MatchFunnel } from "@/components/OrchestratorPanel";
import { TalentReachPanel } from "@/components/TalentReachPanel";
import { Plus, WarningDiamond, UsersThree, PaperPlaneTilt, ChartBar, ChatText, Trash, MagicWand } from "@phosphor-icons/react";
import { COUNTRIES, ARRANGEMENTS, EMPLOYMENT_TYPES, REMOTE_SCOPES, REGIONS, OFFICE_FREQUENCIES, TRAVEL_LEVELS, EMPLOYMENT_MECHANISMS, LOCATION_RATIONALES, TIMEZONE_OFFSETS } from "@/lib/constants";

const BLANK = { title: "", work_arrangement: "remote", remote_countries: [], required_residence_country: "",
  work_authorization_country: "", relocation_required: "unknown", employment_type: "full-time",
  mandatory_qualifications: [], preferred_qualifications: [], flexible_qualifications: [], mandatory_video: false, description: "",
  remote_scope: "", remote_region: "", office_frequency: "", office_days_per_week: null, timezone_offset: null,
  travel_frequency: "", employment_mechanism: "", location_rationale: "", visibility: "matching", assess_in_interview: [] };

export default function EmployerWorkspace() {
  const { user } = useAuth();
  const { ent, usage, refresh: refreshEnt } = useEntitlements();
  const allowed = user?.role === "employer" || user?.role === "admin";
  const atRoleLimit = allowed && ent.max_active_roles !== undefined && ent.max_active_roles !== -1
    && (usage.active_roles ?? 0) >= ent.max_active_roles;
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [cands, setCands] = useState(null);
  const [meta, setMeta] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [reach, setReach] = useState(null);
  const [reachJob, setReachJob] = useState(null);
  const [preJob, setPreJob] = useState(null);
  const [preQs, setPreQs] = useState([]);
  const [preLoading, setPreLoading] = useState(false);
  const load = () => api.get("/employer/jobs").then((r) => setJobs(r.data.jobs));
  useEffect(() => { if (allowed) load(); }, [allowed]);
  if (user && !allowed) return <Navigate to="/dashboard" replace />;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      const { data } = await api.post("/employer/jobs", form);
      toast.success(data.contradictions.length ? `Posted — ${data.contradictions.length} contradiction(s) flagged` : "Job posted");
      setOpen(false); setForm(BLANK); load(); refreshEnt();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const viewCandidates = async (j) => {
    setActiveJob(j); setCands(null); setMeta(null);
    const { data } = await api.get(`/employer/jobs/${j.id}/candidates`);
    setCands(data.candidates); setMeta({ plan: data.plan, funnel: data.funnel });
  };
  const invite = async (c, type) => {
    try { await api.post("/b2b/invite", { candidate_id: c.candidate_id, job_id: activeJob.id, type });
      toast.success(`${type === "invite_to_apply" ? "Invite to apply" : "Contact request"} sent to ${c.name}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const viewReach = async (j) => {
    setReachJob(j); setReach(null);
    const { data } = await api.get(`/employer/jobs/${j.id}/talent-reach`);
    setReach(data);
  };
  const changeStatus = async (j, status) => {
    try { await api.put(`/employer/jobs/${j.id}/status`, { status });
      toast.success(`Role ${status}`); load(); refreshEnt(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const renewJob = async (j) => {
    try { await api.post(`/employer/jobs/${j.id}/renew`, {});
      toast.success("Role renewed"); load(); refreshEnt(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const duplicateJob = async (j) => {
    try { await api.post(`/employer/jobs/${j.id}/duplicate`);
      toast.success("Duplicated as draft"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openPre = (j) => {
    setPreJob(j);
    setPreQs((j.pre_interview_questions || []).map((q) => (typeof q === "string" ? { question: q } : q)));
  };
  const suggestPre = async () => {
    setPreLoading(true);
    try { const { data } = await api.post(`/employer/jobs/${preJob.id}/pre-interview/suggest`);
      setPreQs((p) => [...p, ...(data.questions || [])]); toast.success("AI suggestions added — review and edit before saving"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPreLoading(false); }
  };
  const savePre = async () => {
    try { await api.put(`/employer/jobs/${preJob.id}/pre-interview`, { questions: preQs });
      toast.success("Pre-interview questions saved"); setPreJob(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Employer workspace" subtitle="Post roles with clear rules. See candidates who genuinely qualify — eligibility first, then fit."
        action={<Button onClick={() => setOpen(true)} disabled={atRoleLimit} data-testid="create-job-btn" className="bg-slate-900 hover:bg-slate-800 gap-2"><Plus weight="bold" /> Post a job</Button>} />
      {atRoleLimit && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2.5" data-testid="role-limit-banner">
          <span className="text-sm text-indigo-800">You are using all active role slots included in your current plan ({ent.max_active_roles}). Close or pause an existing role, or upgrade to publish another.</span>
          <Link to="/pricing"><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700">Upgrade</Button></Link>
        </div>
      )}
      {ent.max_active_roles !== undefined && (
        <p className="mb-3 text-sm text-slate-500" data-testid="role-capacity">Role capacity: <span className="font-semibold text-slate-800">{usage.active_roles ?? 0} of {ent.max_active_roles === -1 ? "∞" : ent.max_active_roles} active roles used</span> · {ent.plan_label}</p>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="employer-jobs-list">
        {jobs.length === 0 && <p className="p-8 text-sm text-slate-500 text-center">No roles yet. Post your first job.</p>}
        {jobs.map((j) => (
          <div key={j.id} className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100 last:border-0" data-testid={`emp-job-${j.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-slate-900 break-words min-w-0">{j.title}</p>
                <StatusBadge status={j.status} />
              </div>
              <p className="text-sm text-slate-600">{[j.city, j.country].filter(Boolean).join(", ") || (j.work_arrangement || "").toUpperCase()} · {j.employment_type}</p>
              {j.status === "active" && j.expires_at && <p className="mt-0.5 text-xs text-slate-400" data-testid={`emp-job-expiry-${j.id}`}>Expires {new Date(j.expires_at).toLocaleDateString()}</p>}
              {j.status === "expired" && <p className="mt-0.5 text-xs text-red-600">Expired — renew to reactivate. History preserved.</p>}
              {j.contradictions?.length > 0 && <p className="mt-1 text-xs text-orange-700 flex items-center gap-1"><WarningDiamond weight="fill" /> {j.contradictions.length} contradiction(s) flagged</p>}
            </div>
            <Button variant="outline" size="sm" onClick={() => viewCandidates(j)} data-testid={`view-candidates-${j.id}`} className="border-slate-300 gap-1.5"><UsersThree /> Candidates</Button>
            <Button variant="outline" size="sm" onClick={() => openPre(j)} data-testid={`pre-interview-${j.id}`} className="border-slate-300 gap-1.5"><ChatText /> Pre-interview</Button>
            <Button variant="outline" size="sm" onClick={() => viewReach(j)} data-testid={`talent-reach-${j.id}`} className="border-slate-300 gap-1.5"><ChartBar /> Talent Reach</Button>
            {j.status === "active" && <Button variant="ghost" size="sm" onClick={() => changeStatus(j, "paused")} data-testid={`pause-${j.id}`} className="text-slate-600">Pause</Button>}
            {["paused", "draft"].includes(j.status) && <Button variant="ghost" size="sm" onClick={() => changeStatus(j, "active")} data-testid={`publish-${j.id}`} className="text-emerald-700">Publish</Button>}
            {["expired", "closed"].includes(j.status) && <Button variant="ghost" size="sm" onClick={() => renewJob(j)} data-testid={`renew-${j.id}`} className="text-indigo-700">Renew</Button>}
            {j.status !== "closed" && j.status !== "expired" && <Button variant="ghost" size="sm" onClick={() => changeStatus(j, "closed")} data-testid={`close-${j.id}`} className="text-slate-500">Close</Button>}
            <Button variant="ghost" size="sm" onClick={() => duplicateJob(j)} data-testid={`duplicate-${j.id}`} className="text-slate-500">Duplicate</Button>
          </div>
        ))}
      </div>

      {/* Create job wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Post a job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="job-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Work arrangement</Label>
                <Select value={form.work_arrangement} onValueChange={(v) => set("work_arrangement", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-arrangement"><SelectValue /></SelectTrigger>
                  <SelectContent>{ARRANGEMENTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Employment type</Label>
                <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{EMPLOYMENT_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label>Remote from countries (leave empty = unspecified)</Label>
              <ChipsInput value={form.remote_countries} onChange={(v) => set("remote_countries", v)} placeholder="Add a country" testid="job-remote-countries" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Remote scope</Label>
                <Select value={form.remote_scope || "none"} onValueChange={(v) => set("remote_scope", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-remote-scope"><SelectValue placeholder="Unspecified" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unspecified</SelectItem>{REMOTE_SCOPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Region (if scope = region)</Label>
                <Select value={form.remote_region || "none"} onValueChange={(v) => set("remote_region", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-remote-region"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{REGIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Office attendance</Label>
                <Select value={form.office_frequency || "none"} onValueChange={(v) => set("office_frequency", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-office-frequency"><SelectValue placeholder="Unspecified" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unspecified</SelectItem>{OFFICE_FREQUENCIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Office location (city)</Label>
                <Input className="mt-1.5" value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="job-office-city" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Timezone (UTC offset)</Label>
                <Select value={form.timezone_offset != null ? String(form.timezone_offset) : "none"} onValueChange={(v) => set("timezone_offset", v === "none" ? null : Number(v))}>
                  <SelectTrigger className="mt-1.5" data-testid="job-timezone"><SelectValue placeholder="Unspecified" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unspecified</SelectItem>{TIMEZONE_OFFSETS.map((o) => <SelectItem key={o} value={String(o)}>{`UTC${o >= 0 ? "+" : ""}${o}`}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Travel</Label>
                <Select value={form.travel_frequency || "none"} onValueChange={(v) => set("travel_frequency", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-travel"><SelectValue placeholder="Unspecified" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unspecified</SelectItem>{TRAVEL_LEVELS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employment mechanism</Label>
                <Select value={form.employment_mechanism || "none"} onValueChange={(v) => set("employment_mechanism", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-mechanism"><SelectValue placeholder="Unspecified" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unspecified</SelectItem>{EMPLOYMENT_MECHANISMS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Location restriction reason</Label>
                <Select value={form.location_rationale || "none"} onValueChange={(v) => set("location_rationale", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="job-rationale"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{LOCATION_RATIONALES.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Required residence country</Label>
                <Select value={form.required_residence_country || "none"} onValueChange={(v) => set("required_residence_country", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{COUNTRIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Work authorization country</Label>
                <Select value={form.work_authorization_country || "none"} onValueChange={(v) => set("work_authorization_country", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{COUNTRIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label>Mandatory requirements</Label><ChipsInput value={form.mandatory_qualifications} onChange={(v) => set("mandatory_qualifications", v)} testid="job-mandatory" /></div>
            <div><Label>Preferred requirements</Label><ChipsInput value={form.preferred_qualifications} onChange={(v) => set("preferred_qualifications", v)} testid="job-preferred" /></div>
            <div><Label>Flexible requirements (met = flexibility used)</Label><ChipsInput value={form.flexible_qualifications} onChange={(v) => set("flexible_qualifications", v)} testid="job-flexible" /></div>
            <div><Label>Assess in pre-interview (not a CV filter)</Label><ChipsInput value={form.assess_in_interview} onChange={(v) => set("assess_in_interview", v)} testid="job-assess-interview" /><p className="text-xs text-slate-400 mt-1">Things better checked with a question than a CV — these seed AI pre-interview suggestions and never auto-reject a candidate.</p></div>
            <div><Label>Description</Label><Textarea rows={4} className="mt-1.5" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="job-description" /></div>
            <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2.5 cursor-pointer mt-1" data-testid="job-visibility-row">
              <span className="text-sm font-medium text-slate-800">List publicly to candidates<span className="block text-xs font-normal text-slate-400">Off = B2B matching only (candidates reached via invitations). On = shown in candidate search with full transparency.</span></span>
              <input type="checkbox" checked={form.visibility === "public"} onChange={(e) => set("visibility", e.target.checked ? "public" : "matching")} data-testid="job-visibility-public" />
            </label>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.title} data-testid="save-job-btn" className="bg-slate-900 hover:bg-slate-800">Post job</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Candidates drawer */}
      <Sheet open={!!activeJob} onOpenChange={(o) => !o && setActiveJob(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="candidates-drawer">
          <SheetHeader><SheetTitle className="font-heading">Matched candidates</SheetTitle></SheetHeader>
          <p className="text-sm text-slate-600 mt-1">{activeJob?.title} · eligibility-first. Contact & documents stay private until the candidate accepts.</p>
          {meta && (
            <div className="mt-4 space-y-3">
              <SearchPlanCard plan={meta.plan} kind="candidates" />
              <MatchFunnel funnel={meta.funnel} kind="candidates" />
            </div>
          )}
          {cands === null && <p className="mt-6 text-sm text-slate-400">Matching…</p>}
          {cands && cands.length === 0 && <p className="mt-6 text-sm text-slate-500">No discoverable candidates qualify yet.</p>}
          <div className="mt-4 space-y-3">
            {cands?.map((c) => (
              <div key={c.candidate_id} className="rounded-lg border border-slate-200 p-4" data-testid={`candidate-${c.candidate_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{c.name} <span className="text-xs font-normal text-slate-400">· {c.country || "—"}</span></p>
                    <p className="text-xs text-slate-500">{c.headline || "Candidate"}</p>
                  </div>
                  <div className="text-right"><EligibilityPill status={c.eligibility_status} /><div className="mt-1"><MatchScore score={c.match_score} /></div></div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">{c.top_skills.map((s, i) => <span key={i} className="text-xs bg-slate-100 rounded px-2 py-0.5 text-slate-600">{s}</span>)}</div>
                {c.strong_matches?.length > 0 && <p className="mt-2 text-xs text-emerald-700">✓ {c.strong_matches.map((m) => m.text).join(", ")}</p>}
                {c.flex_used?.length > 0 && <p className="mt-1 text-xs text-blue-700" data-testid="cand-flex-used">⇄ Flexibility used: {c.flex_used.map((f) => f.area).join(", ")}</p>}
                {c.gaps?.length > 0 && <p className="mt-1 text-xs text-slate-500">Gaps: {c.gaps.map((m) => m.text).join(", ")}</p>}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => invite(c, "invite_to_apply")} data-testid={`invite-${c.candidate_id}`} className="bg-slate-900 hover:bg-slate-800 gap-1"><PaperPlaneTilt /> Invite to apply</Button>
                  <Button size="sm" variant="outline" onClick={() => invite(c, "contact_request")} className="border-slate-300">Request contact</Button>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Talent Reach drawer */}
      <Sheet open={!!reachJob} onOpenChange={(o) => !o && setReachJob(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="talent-reach-drawer">
          <SheetHeader><SheetTitle className="font-heading">Talent Reach</SheetTitle></SheetHeader>
          <p className="text-sm text-slate-600 mt-1">{reachJob?.title}</p>
          <div className="mt-4"><TalentReachPanel data={reach} /></div>
        </SheetContent>
      </Sheet>

      {/* Pre-interview questions */}
      <Dialog open={!!preJob} onOpenChange={(o) => !o && setPreJob(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pre-interview-dialog">
          <DialogHeader><DialogTitle>Pre-interview questions</DialogTitle>
            <DialogDescription>Job-specific questions you control — AI suggests, you edit. Never asked automatically; answers never auto-reject a candidate.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">{preJob?.title} — AI suggests job-specific questions from your role criteria. You review, edit, remove or add your own. Nothing is asked automatically and answers never auto-reject a candidate.</p>
          <div className="flex gap-2 my-3">
            <Button size="sm" onClick={suggestPre} disabled={preLoading} data-testid="pre-suggest-btn" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"><MagicWand weight="fill" /> {preLoading ? "Generating…" : "Suggest with AI"}</Button>
            <Button size="sm" variant="outline" onClick={() => setPreQs((p) => [...p, { question: "" }])} data-testid="pre-add-btn" className="border-slate-300 gap-1.5"><Plus weight="bold" /> Add question</Button>
          </div>
          {preQs.length === 0 && <p className="text-sm text-slate-400">No questions yet. Suggest with AI or add your own.</p>}
          <div className="space-y-2">
            {preQs.map((q, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5" data-testid={`pre-q-${i}`}>
                <div className="flex-1">
                  <Textarea rows={2} value={q.question} onChange={(e) => setPreQs((p) => p.map((x, k) => k === i ? { ...x, question: e.target.value } : x))} data-testid={`pre-q-input-${i}`} placeholder="Question" />
                  {q.criterion && <p className="text-[11px] text-slate-400 mt-1">Ties to: {q.criterion}</p>}
                </div>
                <Button variant="ghost" size="icon" onClick={() => setPreQs((p) => p.filter((_, k) => k !== i))} data-testid={`pre-remove-${i}`} className="text-slate-400 hover:text-red-600"><Trash /></Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={savePre} data-testid="pre-save-btn" className="bg-slate-900 hover:bg-slate-800">Save questions</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
  expired: "bg-red-50 text-red-700 border-red-200",
  archived: "bg-slate-100 text-slate-400 border-slate-200",
};

function StatusBadge({ status }) {
  const s = status || "active";
  return <span className={`text-[11px] font-medium rounded-full border px-2 py-0.5 capitalize ${STATUS_STYLES[s] || STATUS_STYLES.draft}`} data-testid={`status-badge-${s}`}>{s}</span>;
}
